import GraphQLClient from 'ts/GraphQLClient'
import { RepoParams } from 'ts/types'
import { QuickSuiteChatEmbedData, QuickSuiteChatRequestOptions } from '../types'
import { GET_QUICK_SUITE_CHAT_URL } from './getQuickSuiteChatUrl'

interface BackendChatData {
  EmbedUrl: string
  RequestId?: string
  Status?: string
  AgentArn?: string
}

interface GetQuickSuiteChatUrlResponse {
  getQuickSuiteChatUrl: {
    data: BackendChatData | null
    error: { code?: string; message?: string; statusCode?: number } | null
  }
}

function toEmbedData(data: BackendChatData | null): QuickSuiteChatEmbedData {
  if (!data?.EmbedUrl) {
    throw new Error('Missing EmbedUrl from QuickSuite chat API')
  }
  return {
    embedUrl: data.EmbedUrl,
    requestId: data.RequestId,
    status: data.Status,
    agentArn: data.AgentArn
  }
}

export default class QuickSuiteChatRepo {
  private client: GraphQLClient

  constructor({ GRAPHQL_URL, GRAPHQL_API_KEY }: Pick<RepoParams, 'GRAPHQL_URL' | 'GRAPHQL_API_KEY'>) {
    this.client = new GraphQLClient({ url: GRAPHQL_URL, apiKey: GRAPHQL_API_KEY })
  }

  /**
   * Request embed URL from the backend. Backend QuickSuiteChatInput only has 'username'.
   * options (agentId/agentArn) are for future use if the backend adds them; agent selection
   * can still be done via the embed's agentArn prop (frontend override).
   */
  async getQuickSuiteChatUrl(
    username: string,
    _options?: QuickSuiteChatRequestOptions
  ): Promise<QuickSuiteChatEmbedData> {
    const response = await this.client.request<GetQuickSuiteChatUrlResponse>({
      query: GET_QUICK_SUITE_CHAT_URL,
      variables: { username }
    })

    const result = response.getQuickSuiteChatUrl
    if (result?.error) {
      throw new Error(result.error.message || 'Failed to get QuickSuite chat URL')
    }
    return toEmbedData(result?.data ?? null)
  }
}
